<script setup lang="ts">
import { computed } from 'vue';
import Message from 'primevue/message';
import { LotKindSchema } from '@neon-one/auctions-types';
import type { PlacementCollect } from '@neon-one/auctions-types';

import { useSettlementStore } from '../../store';
import { ReassignMode, type ReassignModeValue } from '../../item-actions';
import {
    consequenceMessages,
    heldOnLineCents,
    pricingNote,
    placementMessage,
    type PlacementPreview,
} from '../../reassign-consequences';
import { chargeInFlight } from '../../payment-status';
import { firstNameOf, type ReassignSubject, type TargetRow } from '../../reassign-target';

const props = withDefaults(defineProps<{
    subject: ReassignSubject | null;
    selected: TargetRow | null;
    mode?: ReassignModeValue;
    priceCents?: number | null;
    collect?: PlacementCollect | null;
    enhanced?: boolean;
}>(), { mode: ReassignMode.SendOffer, priceCents: null, collect: null, enhanced: false });

const INFO_MESSAGE_SEVERITY = 'info';
const store = useSettlementStore();

const noun = computed(() => props.subject?.lotRef.kind ?? LotKindSchema.enum.item);

// A standing offer on this lot; a new placement supersedes it.
const standingOffer = computed(() => {
    if (!props.subject?.lineId) return null;
    const offer = store.reassignmentFor(props.subject.lineId);
    return offer && offer.mode === ReassignMode.SendOffer ? offer : null;
});

// Money that would go back if the lot moved now — collected, or submitted and
// still clearing. Zero for a stranded lot: there is no live line holding
// anything.
const heldCents = computed(() => {
    const subject = props.subject;
    if (!subject?.holderName || !subject.status) return 0;
    return heldOnLineCents({
        status: subject.status,
        amountCents: subject.amountCents,
        refundedCents: subject.refundedCents,
    });
});

// Rendered in BOTH modes. Send-offer is not the money-free path it reads as:
// an admin offering a paid lot performs the refund at offer creation, so hiding
// these in offer mode would move real money silently. Beside the placement
// summary only the warnings stay: a refund going back or an offer ending.
const consequences = computed(() =>
    consequenceMessages({
        holderName: props.subject?.holderName ?? null,
        invoiceNumber: props.subject?.invoiceNumber ?? null,
        heldCents: heldCents.value,
        chargeInFlight: props.subject?.status ? chargeInFlight(props.subject.status) : false,
        standingOfferName: standingOffer.value?.targetName ?? null,
        noun: noun.value,
    }).filter((c) => !props.enhanced || c.severity !== INFO_MESSAGE_SEVERITY),
);

const pricing = computed(() =>
    !props.enhanced && props.selected?.inherited
        ? pricingNote({
                firstName: firstNameOf(props.selected.name),
                amountCents: props.selected.amountCents,
                fmvCents: props.subject?.fmvCents ?? null,
                noun: noun.value,
            })
        : null,
);

const preview = computed<PlacementPreview | null>(() => {
    const subject = props.subject;
    const selected = props.selected;
    const price = props.priceCents;
    if (!props.enhanced || !subject || !selected || price === null || price <= 0) return null;
    return {
        subject,
        mode: props.mode,
        name: selected.name,
        firstName: firstNameOf(selected.name),
        priceCents: price,
        openInvoiceNumber: selected.openInvoiceNumber,
        openInvoice: selected.openInvoice,
        collect: props.collect,
        cardLabel: selected.cardLabel,
    };
});
const placement = computed(() => preview.value ? placementMessage(preview.value) : null);
</script>

<template>
    <!-- What confirming does to where the lot sits now. Rendered in both modes:
              sending an offer on a paid lot moves money too. -->
    <Message
        v-for="c in consequences"
        :key="c.kind"
        :severity="c.severity"
        :closable="false"
        :data-testid="`ri__consequence-${c.kind}`"
    >
        <span :class="{ 'ri__info-copy': c.severity === INFO_MESSAGE_SEVERITY }">{{ c.text }}</span>
    </Message>

    <!-- Only for a candidate who never bid — the basis decides the bill. -->
    <p v-if="pricing" class="ri__muted text-caption" data-testid="ri__pricing">{{ pricing }}</p>

    <Message
        v-if="placement"
        :severity="placement.severity"
        :closable="false"
        data-testid="ri__consequence-placement"
    >
        <span class="ri__info-copy">{{ placement.text }}</span>
    </Message>
</template>

<style scoped>
.ri__info-copy {
    color: var(--app-text-primary);
}
.ri__muted {
    margin: 0;
    color: var(--app-text-muted);
}
</style>
